import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import { useTheme } from 'styled-components';
import type { IconType } from 'react-icons';
import { FaUser, FaSun, FaMoon, FaDesktop } from 'react-icons/fa';
import { Button } from '../StyledComponents';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCsEvent } from '../client/useEvents';
import { useThemePreference } from '../theme';
import type { ThemePreference } from '../theme';
import type { User } from '../auth/AuthContext';

const MOBILE_BREAKPOINT = 576;

interface ThemeOption {
  value: ThemePreference;
  Icon: IconType;
  labelId: string;
}

const themeOptions: ThemeOption[] = [
  { value: 'light', Icon: FaSun, labelId: 'theme.light' },
  { value: 'dark', Icon: FaMoon, labelId: 'theme.dark' },
  { value: 'system', Icon: FaDesktop, labelId: 'theme.system' },
];

function ThemeToggle() {
  const { preference, setPreference } = useThemePreference();
  const intl = useIntl();
  const theme = useTheme();

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      padding: '4px',
      borderRadius: '8px',
      background: theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    }}>
      {themeOptions.map(({ value, Icon, labelId }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            onClick={() => setPreference(value)}
            title={intl.formatMessage({ id: labelId, defaultMessage: value })}
            aria-label={intl.formatMessage({ id: labelId, defaultMessage: value })}
            aria-pressed={active}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '28px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              background: active ? theme.colors.primary : 'transparent',
              color: active ? theme.colors.textInverse : theme.colors.textSecondary,
            }}
          >
            <Icon size={13} />
          </button>
        );
      })}
    </div>
  );
}

interface HamburgerMenuProps {
  loggedIn: boolean;
  user: User | null;
  openLoginModal: () => void;
  openSignupModal: () => void;
  closeAllModals: () => void;
  onAccountClick: () => void;
  windowWidth: number;
}

export default function HamburgerMenu({
  loggedIn,
  user,
  openLoginModal,
  openSignupModal,
  closeAllModals,
  onAccountClick,
  windowWidth
}: HamburgerMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const hamburgerRef = useRef<HTMLDivElement>(null);
  const fireCsEvent = useCsEvent();
  const theme = useTheme();
  const isMobile = windowWidth < MOBILE_BREAKPOINT;

  const handleHamburgerClick = () => {
    setMenuOpen((open) => !open);
    fireCsEvent({
      category: 'Header',
      action: 'Hamburger Menu',
      label: menuOpen ? 'Close' : 'Open'
    });
  };

  const handleMenuAccountClick = () => {
    setMenuOpen(false);
    onAccountClick();
  };

  useLayoutEffect(() => {
    if (!menuOpen || !isMobile || !hamburgerRef.current) return;
    const rect = hamburgerRef.current.getBoundingClientRect();
    const gap = 8;
    const rightInset = 12;
    setDropdownPosition({
      top: rect.bottom + gap,
      right: rightInset,
    });
  }, [menuOpen, isMobile]);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (hamburgerRef.current && !hamburgerRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div style={{ position: 'relative' }} ref={hamburgerRef}>
      <FaUser
        className="ml-1 mr-1"
        onClick={handleHamburgerClick}
        size={windowWidth < 576 ? '2.5em' : '2.2em'}
        style={{
          cursor: 'pointer',
          color: menuOpen ? theme.colors.warning : theme.colors.menuText,
          transition: 'color 0.2s',
          padding: '6px',
          borderRadius: '50%',
          background: menuOpen ? `${theme.colors.warning}26` : 'transparent',
          boxShadow: menuOpen ? theme.shadows?.subtle : 'none',
          outline: menuOpen ? `2px solid ${theme.colors.warning}` : 'none',
        }}
        aria-label="Menüyü aç/kapat"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleHamburgerClick(); }}
      />
      {menuOpen && (
        <div style={{
          position: isMobile ? 'fixed' : 'absolute',
          top: isMobile ? dropdownPosition.top : '2.7em',
          right: isMobile ? dropdownPosition.right : 0,
          background: theme.colors.menuBg,
          boxShadow: theme.shadows?.overlay ?? '0 8px 32px rgba(0,0,0,0.18)',
          borderRadius: '12px',
          zIndex: 10001,
          minWidth: '160px',
          padding: '0.5em 0',
          animation: 'fadeInMenu 0.18s',
          border: `1px solid ${theme.colors.menuBorder}`,
        } as React.CSSProperties}>
          {loggedIn ? (
            <>
              <div style={{
                width: '100%',
                padding: '0.7em 1.2em',
                color: theme.colors.menuTextSecondary,
                fontSize: '0.98em',
                fontWeight: 500,
                borderBottom: `1px solid ${theme.colors.menuUserBorder}`,
                marginBottom: 2,
                background: theme.colors.menuUserBg,
                borderRadius: '8px 8px 0 0',
                letterSpacing: '0.01em',
              }}>
                👋 {user?.username}
              </div>
              <Button
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  color: theme.colors.menuText,
                  padding: '0.7em 1.2em',
                  fontSize: '1.05em',
                  borderRadius: '0',
                  transition: 'background 0.15s',
                }}
                onClick={handleMenuAccountClick}
                onMouseOver={e => { e.currentTarget.style.background = theme.colors.menuHover ?? 'transparent'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
              >
                <FormattedMessage id="header.account" defaultMessage="Hesabım" />
              </Button>
            </>
          ) : (
            <>
              <Button
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  color: theme.colors.menuText,
                  padding: '0.7em 1.2em',
                  fontSize: '1.05em',
                  borderRadius: '0',
                  transition: 'background 0.15s',
                }}
                onClick={() => { setMenuOpen(false); openLoginModal(); }}
                onMouseOver={e => { e.currentTarget.style.background = theme.colors.menuHover ?? 'transparent'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
              >
                <FormattedMessage id="header.signin" defaultMessage="Giriş Yap" />
              </Button>
              <Button
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  color: theme.colors.menuText,
                  padding: '0.7em 1.2em',
                  fontSize: '1.05em',
                  borderRadius: '0',
                  transition: 'background 0.15s',
                }}
                onClick={() => { setMenuOpen(false); closeAllModals(); openSignupModal(); }}
                onMouseOver={e => { e.currentTarget.style.background = theme.colors.menuHover ?? 'transparent'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
              >
                <FormattedMessage id="header.signup" defaultMessage="Kayıt Ol" />
              </Button>
            </>
          )}
          <div style={{
            borderTop: `1px solid ${theme.colors.menuBorder}`,
            padding: '0.6em 1.2em 0.3em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
          }}>
            <span style={{
              fontSize: '0.85em',
              color: theme.colors.menuTextSecondary,
              fontWeight: 500,
            }}>
              <FormattedMessage id="theme.label" defaultMessage="Tema" />
            </span>
            <ThemeToggle />
          </div>
        </div>
      )}
      <style>{`
        @keyframes fadeInMenu {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
